import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { trackedFunction } from 'reactiveweb/function';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import { VStack } from 'tio-ui/components/layout';
import {
  PhArrowRight,
  PhClipboardText,
  PhUploadSimple,
  PhTrendUp,
  PhBuildings,
  PhCheckCircle,
  PhFileText,
  PhListChecks,
  PhQuestion,
} from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { t } from 'ember-intl';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { RecommendationExtension as RecomendationModel } from 'tio-data/data/resources/recommendation.ext';
import { on } from '@ember/modifier';
import PslfBorrowerFormsTable from 'tio-employee/components/pslf/borrower-forms-table';
import type { IntlService } from 'ember-intl';
import Card from 'tio-ui/components/card';
import FaqAccordion from 'tio-ui/components/faq-accordion';
import type { AccordionItem } from 'tio-ui/components/faq-accordion';
import SectionHeader from 'tio-ui/components/section-header';
import Text from 'tio-ui/components/text';
import type { TemplateOnlyComponent } from '@ember/component/template-only';
import HeroDashboard from 'tio-employee/components/pslf/hero-dashboard';
import PslfFormStatusAlerts from 'tio-employee/components/pslf/form-status-alerts';
import PslfRecommendations from 'tio-employee/components/pslf/recommendations';
import PslfQualifyingEmployment from 'tio-employee/components/pslf/qualifying-employment';
import PslfDebugPanel from 'tio-employee/components/reimagined/pslf/debug-panel';

/**
 * Step row — icon-in-circle + title + description. Co-located here
 * (3 call sites in the "How it works" steps section).
 */
type CircleIntent = 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';
// Matches the canonical phosphor icon signature in
// `test-app/types/ember-phosphor-icons.d.ts` so Glint accepts every
// PhFoo without complaining about missing optional args (class/weight).
type IconComponent = ComponentLike<{
  Args: {
    size?: number | string;
    weight?: string;
    class?: string;
  };
  Element: SVGElement;
}>;

interface StepRowSignature {
  Args: {
    icon: IconComponent;
    intent: CircleIntent;
    titleKey: string;
    descriptionKey: string;
  };
}

const StepRow: TemplateOnlyComponent<StepRowSignature> = <template>
  <div class="tio-row-top-loose">
    <div class="app-tinted-circle app-tinted-circle--md app-tinted-circle--{{@intent}}">
      <@icon @size={{20}} />
    </div>
    <div>
      <Text @weight="semibold" class="mb-1">
        {{t @titleKey}}
      </Text>
      <Text @variant="muted">
        {{t @descriptionKey}}
      </Text>
    </div>
  </div>
</template>;

/**
 * Eligibility card — centered icon-in-circle + title + description.
 * Co-located here (3 call sites in the eligibility section).
 */
interface EligibilityCardSignature {
  Args: {
    icon: IconComponent;
    intent: CircleIntent;
    titleKey: string;
    descriptionKey: string;
  };
}

const EligibilityCard: TemplateOnlyComponent<EligibilityCardSignature> = <template>
  <div class="text-center">
    <div
      class="app-tinted-circle app-tinted-circle--lg app-tinted-circle--{{@intent}} mx-auto mb-3"
    >
      <@icon @size={{24}} />
    </div>
    <Text @weight="semibold" class="mb-1">
      {{t @titleKey}}
    </Text>
    <Text @variant="muted" @size="sm">
      {{t @descriptionKey}}
    </Text>
  </div>
</template>;

interface S {
  Args: {
    model: {
      loanSummary: LoanSummary;
      accounts: AccountModel[];
      pslfProfile: PslfProfileModel;
      pslfForms: PslfFormModel[];
      person: PersonModel;
      mostRecentRecommendation?: RecomendationModel;
      debugLinkLoans: boolean;
    };
  };
}

export default class PslfDashboardIndexComponent extends Component<S> {
  @service declare intl: IntlService;
  @service('answer-profile') declare answerProfileService: AnswerProfileService;

  answerProfileState = trackedFunction(this, async () => {
    return this.answerProfileService.findOrCreateAnswerProfileForCurrentUser();
  });

  get answerProfile() {
    return this.answerProfileState.value ?? undefined;
  }

  get hasPslfForms() {
    return (this.args.model.pslfForms?.length ?? 0) > 0;
  }

  get debugLinkLoans() {
    return this.args.model.debugLinkLoans ?? false;
  }

  get hasNsldsData() {
    return (this.args.model.person.accounts ?? []).some((account) => account.hasNsldsPslfData);
  }

  get hasActiveRecommendations() {
    const summaries = this.args.model.loanSummary?.summaryByLoanType || [];
    const allPlans = summaries.flatMap((s) => s.repaymentPlans);
    const hasMissing = allPlans.some((p) => p === 'noRepaymentPlan' || !p);

    // In-platform recommendations can't be checked off — always active if present
    const shouldLinkLoans = this.debugLinkLoans || !this.hasNsldsData;
    if (shouldLinkLoans || hasMissing) return true;

    // External recommendations — active only if not checked off by user
    const profile = this.answerProfile;
    const shouldConsolidate = summaries.some((s) => s.loanType !== 'direct');
    const validPlans = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const shouldChangeRepayment =
      !hasMissing &&
      allPlans.length > 0 &&
      [...new Set(allPlans)].every((p) => !validPlans.includes(p));
    const needsToRecertify = !!this.args.model.mostRecentRecommendation;

    if (shouldConsolidate && !profile?.consolidatedLoansAt) return true;
    if (shouldChangeRepayment && !profile?.changedRepaymentPlanAt) return true;
    if (needsToRecertify && !profile?.recertifiedIncomeAt) return true;

    return false;
  }

  get faqItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('pslf.reimagined.faqs.what_is_pslf.question'),
        answer: this.intl.t('pslf.reimagined.faqs.what_is_pslf.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.question'),
        answer: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.qualifying_employers.question'),
        answer: this.intl.t('pslf.reimagined.faqs.qualifying_employers.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.how_to_apply.question'),
        answer: this.intl.t('pslf.reimagined.faqs.how_to_apply.answer'),
      },
    ];
  }

  <template>
    <VStack @collapsed={{true}}>
      <HeroDashboard
        @pslfProfile={{@model.pslfProfile}}
        @pslfForms={{@model.pslfForms}}
        @loanSummary={{@model.loanSummary}}
        @person={{@model.person}}
        @mostRecentRecommendation={{@model.mostRecentRecommendation}}
      />

      {{#if this.hasPslfForms}}
        {{#if this.hasActiveRecommendations}}
          <PslfRecommendations
            @loanSummary={{@model.loanSummary}}
            @person={{@model.person}}
            @recommendation={{@model.mostRecentRecommendation}}
            @debugLinkLoans={{this.debugLinkLoans}}
            @answerProfile={{this.answerProfile}}
          />
        {{/if}}
      {{/if}}

      {{#if this.hasPslfForms}}
        <PslfFormStatusAlerts @forms={{@model.pslfForms}} class="space-y-3" />
      {{/if}}

      {{#if this.hasPslfForms}}
        <Card @padding="lg" @roundedXl={{true}} class="overflow-x-auto">
          <SectionHeader
            @icon={{PhFileText}}
            @title={{t "pslf.document_repo.my_pslf_forms"}}
            @iconClass="app-text-brand"
            @level={{2}}
          >
            <:trailing>
              <Button
                @appearance="outlined"
                @intent="primary"
                @size="sm"
                data-test-pslf-start-new-form
                {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
              >
                {{t "pslf.reimagined.submit_new_form"}}
                <PhArrowRight @size={{14}} />
              </Button>
            </:trailing>
          </SectionHeader>
          <PslfBorrowerFormsTable @forms={{@model.pslfForms}} />
        </Card>
      {{else}}
        <Card @padding="lg" @roundedXl={{true}}>
          <SectionHeader
            @icon={{PhListChecks}}
            @title={{t "pslf.reimagined.steps.heading"}}
            @iconClass="app-text-brand"
            @level={{2}}
          />
          <div class="space-y-6">
            <StepRow
              @icon={{PhClipboardText}}
              @intent="brand"
              @titleKey="pslf.reimagined.steps.submit_form.title"
              @descriptionKey="pslf.reimagined.steps.submit_form.description"
            />
            <StepRow
              @icon={{PhUploadSimple}}
              @intent="info"
              @titleKey="pslf.reimagined.steps.upload_data.title"
              @descriptionKey="pslf.reimagined.steps.upload_data.description"
            />
            <StepRow
              @icon={{PhTrendUp}}
              @intent="brand"
              @titleKey="pslf.reimagined.steps.track_progress.title"
              @descriptionKey="pslf.reimagined.steps.track_progress.description"
            />
          </div>
        </Card>
      {{/if}}

      {{! Eligibility Section (blank state) or Qualifying Employment (has forms) }}
      {{#if this.hasPslfForms}}
        <PslfQualifyingEmployment @forms={{@model.pslfForms}} />
      {{else}}
        <Card @padding="lg" @roundedXl={{true}}>
          <SectionHeader
            @icon={{PhCheckCircle}}
            @title={{t "pslf.reimagined.eligibility.heading"}}
            @iconClass="app-icon-info"
            @level={{2}}
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <EligibilityCard
              @icon={{PhBuildings}}
              @intent="brand"
              @titleKey="pslf.reimagined.eligibility.qualifying_employer.title"
              @descriptionKey="pslf.reimagined.eligibility.qualifying_employer.description"
            />
            <EligibilityCard
              @icon={{PhCheckCircle}}
              @intent="info"
              @titleKey="pslf.reimagined.eligibility.payments.title"
              @descriptionKey="pslf.reimagined.eligibility.payments.description"
            />
            <EligibilityCard
              @icon={{PhFileText}}
              @intent="warning"
              @titleKey="pslf.reimagined.eligibility.direct_loans.title"
              @descriptionKey="pslf.reimagined.eligibility.direct_loans.description"
            />
          </div>
        </Card>
      {{/if}}

      {{#if this.hasPslfForms}}
        {{#unless this.hasActiveRecommendations}}
          <PslfRecommendations
            @loanSummary={{@model.loanSummary}}
            @person={{@model.person}}
            @recommendation={{@model.mostRecentRecommendation}}
            @debugLinkLoans={{this.debugLinkLoans}}
            @answerProfile={{this.answerProfile}}
          />
        {{/unless}}
      {{/if}}

      {{! FAQ Section }}
      <Card @padding="lg" @roundedXl={{true}}>
        <SectionHeader
          @icon={{PhQuestion}}
          @title={{t "pslf.reimagined.faqs.heading"}}
          @iconClass="app-icon-warning"
          @level={{2}}
        />
        <FaqAccordion @items={{this.faqItems}} />
      </Card>

      <PslfDebugPanel />
    </VStack>
  </template>
}
